import { useEffect, useMemo, useState } from 'react';
import taskService from '../../../services/taskService';
import { equipmentService } from '../../../services/equipmentService';
import { toast } from 'react-toastify';
import { Calendar, User, Clock, AlertCircle, Power, PowerOff } from 'lucide-react';
import RightDrawer from '../../../shared/components/form/RightDrawer';

interface Task {
  _id: string;
  checkpointName?: string;
  checkpointDetails?: {
    name: string;
    frequency: string;
    priority: string;
  };
  status: string;
  startDate: string;
  endDate: string;
  assignedEngineerId?: {
    name: string;
    _id: string;
  };
  outcome: string;
  dueDate: string;
  completedDate?: string;
  displayStatus: string;
  isOverdue: boolean;
}

interface Equipment {
  _id: string;
  equipmentName: string;
  equipmentId: string;
  makeAndModel?: string;
  manufacturer?: string;
  currentStatus?: string;
  status?: 'active' | 'inactive';
  siteLabel?: string;
  serialNumber?: string;
}

interface EquipmentTasksDrawerProps {
  open: boolean;
  onClose: () => void;
  equipment: Equipment | null;
  projectStart?: string;
  projectEnd?: string;
}

export default function EquipmentTasksDrawer({
  open,
  onClose,
  equipment,
  projectStart,
  projectEnd
}: EquipmentTasksDrawerProps) {
  const [loading, setLoading] = useState(false);
  const [tasks, setTasks] = useState<Task[]>([]);
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [startDate, setStartDate] = useState<string>('');
  const [endDate, setEndDate] = useState<string>('');
  const [monthKey, setMonthKey] = useState<string>('');
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [pendingStatus, setPendingStatus] = useState<'active' | 'inactive' | null>(null);
  const [equipmentData, setEquipmentData] = useState<Equipment | null>(equipment);

  useEffect(() => {
    if (open && equipment) {
      fetchTasks();
      setEquipmentData(equipment);
    }
  }, [open, equipment]);

  // Initialize default month window from project start
  useEffect(() => {
    if (!projectStart) return;
    const start = normalizeToISO(projectStart);
    if (!start) return;
    const firstMonthStart = new Date(Date.UTC(start.getUTCFullYear(), start.getUTCMonth(), 1));
    const firstMonthEnd = new Date(Date.UTC(start.getUTCFullYear(), start.getUTCMonth() + 1, 0));
    setStartDate(formatISODate(firstMonthStart));
    setEndDate(formatISODate(firstMonthEnd));
    setMonthKey(monthKeyFromDate(firstMonthStart));
  }, [projectStart]);

  const fetchTasks = async () => {
    if (!equipment?._id) return;
    setLoading(true);
    try {
      const response = await taskService.getTasks({
        equipmentId: equipment._id,
        limit: 500,
        page: 1,
        sortBy: 'startDate:desc'
      });
      
      const taskList = Array.isArray(response?.data?.docs) ? response.data.docs : 
                      Array.isArray(response?.data) ? response.data : [];
      setTasks(taskList);
    } catch (error: any) {
      console.error('Failed to fetch tasks:', error);
      toast.error(error?.message || 'Failed to load tasks');
      setTasks([]);
    } finally {
      setLoading(false);
    }
  };

  const handleStatusChange = (newStatus: 'active' | 'inactive') => {
    setPendingStatus(newStatus);
    setShowConfirmation(true);
  };

  const confirmStatusChange = async () => {
    if (!equipmentData?._id || !pendingStatus) return;
    
    setLoading(true);
    try {
      await equipmentService.updateEquipmentStatus(
        equipmentData._id,
        pendingStatus
      );
      
      setEquipmentData(prev => prev ? { ...prev, status: pendingStatus } : null);
      setShowConfirmation(false);
      setPendingStatus(null);
      
      // Refresh tasks to reflect status changes
      await fetchTasks();
      
      toast.success(`Equipment ${pendingStatus === 'active' ? 'activated' : 'deactivated'} successfully`);
    } catch (error: any) {
      console.error('Failed to update equipment status:', error);
      toast.error(error?.message || 'Failed to update equipment status');
    } finally {
      setLoading(false);
    }
  };

  const cancelStatusChange = () => {
    setShowConfirmation(false);
    setPendingStatus(null);
  };

  const filteredTasks = useMemo(() => {
    const from = startDate ? new Date(startDate) : null;
    const to = endDate ? new Date(endDate) : null;
    
    const filtered = tasks.filter((task) => {
      // Status filter
      if (statusFilter && task.status !== statusFilter) return false;
      
      // Date filter
      const taskDate = task.dueDate || task.startDate;
      const date = taskDate ? new Date(taskDate) : null;
      
      if (from && (!date || date < from)) return false;
      if (to) {
        const endDay = new Date(to);
        endDay.setHours(23, 59, 59, 999);
        if (!date || date > endDay) return false;
      }
      
      return true;
    });
    
    return filtered.sort((a, b) => {
      const dateA = new Date(a.dueDate || a.startDate || 0);
      const dateB = new Date(b.dueDate || b.startDate || 0);
      return dateB.getTime() - dateA.getTime();
    });
  }, [tasks, startDate, endDate, statusFilter]);

  const groupedByDate = useMemo(() => {
    const groups = new Map<string, Task[]>();
    
    filteredTasks.forEach((task) => {
      const date = formatDate(task.dueDate || task.startDate);
      const key = date || 'No Date';
      
      if (!groups.has(key)) {
        groups.set(key, []);
      }
      groups.get(key)!.push(task);
    });
    
    return Array.from(groups.entries());
  }, [filteredTasks]);

  const getStatusBadge = (task: Task) => {
    const status = task.status;
    let className = '';
    let displayText = task.displayStatus || status;

    switch (status) {
      case 'pass':
        className = 'bg-green-100 text-green-700 border-green-200';
        displayText = 'Completed (Pass)';
        break;
      case 'fail':
        className = 'bg-red-100 text-red-700 border-red-200';
        displayText = 'Completed (Fail)';
        break;
      case 'inprogress':
        className = 'bg-blue-100 text-blue-700 border-blue-200';
        displayText = 'In Progress';
        break;
      case 'hold':
        className = 'bg-yellow-100 text-yellow-700 border-yellow-200';
        displayText = 'On Hold';
        break;
      case 'due':
        className = task.isOverdue 
          ? 'bg-orange-100 text-orange-700 border-orange-200'
          : 'bg-blue-100 text-blue-700 border-blue-200';
        displayText = task.isOverdue ? 'Overdue' : 'Due';
        break;
      case 'unassigned':
        className = 'bg-gray-100 text-gray-700 border-gray-200';
        displayText = 'Unassigned';
        break;
      default:
        className = 'bg-gray-100 text-gray-700 border-gray-200';
    }

    return (
      <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium border ${className}`}>
        {displayText}
      </span>
    );
  };

  return (
    <RightDrawer
      open={open}
      onClose={onClose}
      title="Equipment Tasks"
      size="60"
    >
      {/* Equipment Header Info */}
      <div className="-mt-6 -mx-6 mb-6 p-6 border-b border-gray-200 bg-gradient-to-r from-blue-50 to-indigo-50">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm text-gray-600 mt-1">{equipmentData?.equipmentName}</p>
            <p className="text-xs text-gray-500 mt-0.5">
              ID: {equipmentData?.equipmentId} | {equipmentData?.makeAndModel}
            </p>
          </div>
          <div className="flex items-center gap-3">
            {/* Equipment Status Toggle Button */}
            <button
              onClick={() => handleStatusChange(equipmentData?.status === 'active' ? 'inactive' : 'active')}
              disabled={loading}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-colors ${
                equipmentData?.status === 'active'
                  ? 'bg-red-100 text-red-700 hover:bg-red-200 border border-red-300'
                  : 'bg-green-100 text-green-700 hover:bg-green-200 border border-green-300'
              } ${loading ? 'opacity-50 cursor-not-allowed' : ''}`}
              title={equipmentData?.status === 'active' ? 'Deactivate Equipment' : 'Activate Equipment'}
            >
              {equipmentData?.status === 'active' ? (
                <>
                  <PowerOff size={16} />
                  Deactivate
                </>
              ) : (
                <>
                  <Power size={16} />
                  Activate
                </>
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Equipment Info */}
      <div className="-mx-6 px-6 py-4 bg-gray-50 border-b border-gray-200 mb-6">
        <div className="grid grid-cols-4 gap-4 text-sm">
            <div>
              <span className="text-gray-500">Location:</span>
              <span className="ml-2 font-medium">{equipmentData?.siteLabel || 'N/A'}</span>
            </div>
            <div>
              <span className="text-gray-500">Manufacturer:</span>
              <span className="ml-2 font-medium">{equipmentData?.manufacturer || 'N/A'}</span>
            </div>
            <div>
              <span className="text-gray-500">Current Status:</span>
              <span className={`ml-2 px-2 py-1 rounded-full text-xs font-medium ${
                equipmentData?.currentStatus === 'Operational' 
                  ? 'bg-green-100 text-green-700' 
                  : 'bg-orange-100 text-orange-700'
              }`}>
                {equipmentData?.currentStatus || 'Unknown'}
              </span>
            </div>
            <div>
              <span className="text-gray-500">Equipment Status:</span>
              <span className={`ml-2 px-2 py-1 rounded-full text-xs font-medium ${
                equipmentData?.status === 'active' 
                  ? 'bg-green-100 text-green-700' 
                  : 'bg-red-100 text-red-700'
              }`}>
                {equipmentData?.status === 'active' ? 'Active' : 'Inactive'}
              </span>
            </div>
          </div>
      </div>

      {/* Filters */}
      <div className="-mx-6 px-6 py-4 border-b border-gray-200 bg-white mb-6">
        <div className="flex flex-wrap items-end gap-4 text-sm">
            <div className="flex flex-col">
              <label className="text-xs text-gray-600 mb-1">Month</label>
              <select 
                className="border border-gray-300 rounded-lg px-3 py-2 min-w-[160px] focus:ring-2 focus:ring-blue-500 focus:border-blue-500" 
                value={monthKey} 
                onChange={(e) => {
                  const key = e.target.value; 
                  setMonthKey(key);
                  const [y, m] = key.split('-').map(Number);
                  const s = new Date(Date.UTC(y, m-1, 1));
                  const eom = new Date(Date.UTC(y, m, 0));
                  setStartDate(formatISODate(s)); 
                  setEndDate(formatISODate(eom));
                }}
              >
                {buildMonthRange(projectStart, projectEnd).map(k => (
                  <option key={k} value={k}>{prettyMonth(k)}</option>
                ))}
              </select>
            </div>
            
            <div className="flex flex-col">
              <label className="text-xs text-gray-600 mb-1">Status</label>
              <select 
                className="border border-gray-300 rounded-lg px-3 py-2 min-w-[120px] focus:ring-2 focus:ring-blue-500 focus:border-blue-500" 
                value={statusFilter} 
                onChange={(e) => setStatusFilter(e.target.value)}
              >
                <option value="">All</option>
                <option value="unassigned">Unassigned</option>
                <option value="due">Due</option>
                <option value="inprogress">In Progress</option>
                <option value="hold">On Hold</option>
                <option value="pass">Completed (Pass)</option>
                <option value="fail">Completed (Fail)</option>
              </select>
            </div>
            
            <div className="flex flex-col">
              <label className="text-xs text-gray-600 mb-1">Start Date</label>
              <input 
                type="date" 
                className="border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500" 
                value={startDate} 
                onChange={(e) => setStartDate(e.target.value)} 
              />
            </div>
            
            <div className="flex flex-col">
              <label className="text-xs text-gray-600 mb-1">End Date</label>
              <input 
                type="date" 
                className="border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500" 
                value={endDate} 
                onChange={(e) => setEndDate(e.target.value)}
              />
            </div>
            
            <div className="ml-auto flex items-center gap-2 text-xs text-gray-500">
              {loading && <div className="w-4 h-4 border-2 border-gray-300 border-t-blue-500 rounded-full animate-spin"></div>}
              <span>{filteredTasks.length} tasks</span>
            </div>
        </div>
      </div>

      {/* Tasks Content */}
      <div className="-mx-6">
        {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="w-8 h-8 border-2 border-gray-200 border-t-blue-500 rounded-full animate-spin"></div>
              <span className="ml-3 text-gray-600">Loading tasks...</span>
            </div>
          ) : groupedByDate.length === 0 ? (
            <div className="text-center py-12">
              <AlertCircle size={32} className="text-gray-400 mx-auto mb-3" />
              <h4 className="text-lg font-medium text-gray-900 mb-2">No Tasks Found</h4>
              <p className="text-gray-600">No tasks match the current filters for this equipment.</p>
            </div>
          ) : (
            <div className="divide-y divide-gray-200">
              {groupedByDate.map(([dateStr, taskList]) => (
                <div key={dateStr} className="bg-white">
                  <div className="sticky top-0 bg-gray-100 px-6 py-3 border-b border-gray-200">
                    <div className="flex items-center gap-2">
                      <Calendar size={16} className="text-gray-500" />
                      <h4 className="font-semibold text-gray-900">{dateStr}</h4>
                      <span className="text-sm text-gray-500">({taskList.length} tasks)</span>
                    </div>
                  </div>
                  
                  <div className="divide-y divide-gray-100">
                    {taskList.map((task) => (
                      <div key={task._id} className="p-6 hover:bg-gray-50 transition-colors">
                        <div className="flex items-start justify-between gap-4">
                          <div className="flex-1">
                            <div className="flex items-start gap-3">
                              <div className="flex-1">
                                <h5 className="font-medium text-gray-900 mb-1">
                                  {task.checkpointDetails?.name || task.checkpointName || 'Unnamed Task'}
                                </h5>
                                
                                <div className="flex items-center gap-4 text-sm text-gray-600 mb-2">
                                  {task.checkpointDetails?.frequency && (
                                    <div className="flex items-center gap-1">
                                      <Clock size={14} />
                                      <span>{task.checkpointDetails.frequency}</span>
                                    </div>
                                  )}
                                  
                                  {task.checkpointDetails?.priority && (
                                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                                      task.checkpointDetails.priority === 'High' ? 'bg-red-100 text-red-700' :
                                      task.checkpointDetails.priority === 'Medium' ? 'bg-yellow-100 text-yellow-700' :
                                      'bg-green-100 text-green-700'
                                    }`}>
                                      {task.checkpointDetails.priority} Priority
                                    </span>
                                  )}
                                  
                                  {task.assignedEngineerId && (
                                    <div className="flex items-center gap-1">
                                      <User size={14} />
                                      <span>{task.assignedEngineerId.name}</span>
                                    </div>
                                  )}
                                </div>
                                
                                <div className="flex items-center gap-3 text-sm text-gray-600">
                                  <span>Due: {formatDate(task.dueDate)}</span>
                                  {task.completedDate && (
                                    <span>Completed: {formatDate(task.completedDate)}</span>
                                  )}
                                  {task.outcome && task.outcome !== 'Pending' && (
                                    <span>Outcome: {task.outcome}</span>
                                  )}
                                </div>
                              </div>
                              
                              <div className="flex-shrink-0">
                                {getStatusBadge(task)}
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
      </div>

      {/* Footer */}
      <div className="-mx-6 mt-6 px-6 py-4 border-t border-gray-200 bg-gray-50">
        <div className="flex items-center justify-between text-sm text-gray-600">
          <span>
            Tasks are generated from project-level asset checkpoints
          </span>
          <span>
            {filteredTasks.length} of {tasks.length} tasks shown
          </span>
        </div>
      </div>

      {/* Confirmation Dialog */}
      {showConfirmation && (
        <div className="fixed inset-0 z-[1100] flex items-center justify-center">
          <div className="absolute inset-0 bg-black/50" onClick={cancelStatusChange} />
          <div className="relative bg-white rounded-lg shadow-xl p-6 max-w-md mx-4">
            <div className="flex items-center gap-3 mb-4">
              {pendingStatus === 'active' ? (
                <Power size={24} className="text-green-600" />
              ) : (
                <PowerOff size={24} className="text-red-600" />
              )}
              <h3 className="text-lg font-semibold text-gray-900">
                {pendingStatus === 'active' ? 'Activate' : 'Deactivate'} Equipment
              </h3>
            </div>
            
            <p className="text-gray-600 mb-6">
              Are you sure you want to {pendingStatus === 'active' ? 'activate' : 'deactivate'} the equipment "{equipmentData?.equipmentName}"?
              <br />
              <br />
              <span className="font-medium">
                {pendingStatus === 'active' 
                  ? 'This will activate all non-completed tasks for this equipment.'
                  : 'This will deactivate all non-completed tasks for this equipment and unassign them from engineers.'
                }
              </span>
            </p>
            
            <div className="flex items-center justify-end gap-3">
              <button
                onClick={cancelStatusChange}
                disabled={loading}
                className="px-4 py-2 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={confirmStatusChange}
                disabled={loading}
                className={`px-4 py-2 text-white rounded-lg transition-colors disabled:opacity-50 ${
                  pendingStatus === 'active'
                    ? 'bg-green-600 hover:bg-green-700'
                    : 'bg-red-600 hover:bg-red-700'
                }`}
              >
                {loading ? (
                  <div className="flex items-center gap-2">
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                    {pendingStatus === 'active' ? 'Activating...' : 'Deactivating...'}
                  </div>
                ) : (
                  `${pendingStatus === 'active' ? 'Activate' : 'Deactivate'} Equipment`
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </RightDrawer>
  );
}

// Utility functions
function formatDate(val?: string | Date) {
  if (!val) return '-';
  try {
    const d = typeof val === 'string' ? new Date(val) : val;
    if (Number.isNaN(d.getTime())) return '-';
    return d.toISOString().slice(0, 10);
  } catch {
    return '-';
  }
}

function normalizeToISO(input?: string): Date | null {
  if (!input) return null;
  if (/^\d{4}-\d{2}-\d{2}/.test(input)) {
    const d = new Date(input);
    return isNaN(d.getTime()) ? null : d;
  }
  const m = input.match(/(\d{2})\/(\d{2})\/(\d{4})/);
  if (m) {
    const [, dd, mm, yyyy] = m;
    const d = new Date(`${yyyy}-${mm}-${dd}T00:00:00Z`);
    return isNaN(d.getTime()) ? null : d;
  }
  const d = new Date(input);
  return isNaN(d.getTime()) ? null : d;
}

function formatISODate(d: Date): string {
  return d.toISOString().slice(0, 10);
}

function monthKeyFromDate(d: Date): string {
  return `${d.getUTCFullYear()}-${String(d.getUTCMonth()+1).padStart(2,'0')}`;
}

function buildMonthRange(projectStart?: string, projectEnd?: string): string[] {
  const start = normalizeToISO(projectStart) || new Date();
  const end = normalizeToISO(projectEnd) || start;
  const startYM = new Date(Date.UTC(start.getUTCFullYear(), start.getUTCMonth(), 1));
  const endYM = new Date(Date.UTC(end.getUTCFullYear(), end.getUTCMonth(), 1));
  const list: string[] = [];
  let cur = startYM;
  while (cur.getTime() <= endYM.getTime()) {
    list.push(monthKeyFromDate(cur));
    cur = new Date(Date.UTC(cur.getUTCFullYear(), cur.getUTCMonth()+1, 1));
  }
  if (list.length === 0) list.push(monthKeyFromDate(startYM));
  return list;
}

function prettyMonth(key: string): string {
  const [y, m] = key.split('-').map(Number);
  const d = new Date(Date.UTC(y, (m||1)-1, 1));
  return d.toLocaleString('en-US', { month: 'long', year: 'numeric', timeZone: 'UTC' });
}
